import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const EMPTY_CONFIG = { defaultPercent: 0, groups: [], overrides: [] };

function normalizePercent(value, maxPercent = 20) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round(Math.min(maxPercent, Math.max(0, number)) * 100) / 100;
}

export function normalizeShaveConfig(value, maxPercent = 20) {
  return {
    defaultPercent: normalizePercent(value?.defaultPercent, maxPercent),
    groups: (Array.isArray(value?.groups) ? value.groups : []).map((group) => ({
      groupId: String(group?.groupId || '').trim(),
      name: String(group?.name || '').trim(),
      partners: [...new Set((Array.isArray(group?.partners) ? group.partners : [])
        .map((partner) => String(partner || '').trim())
        .filter(Boolean))],
      percent: normalizePercent(group?.percent, maxPercent),
    })),
    overrides: (Array.isArray(value?.overrides) ? value.overrides : []).map((item) => ({
      partner: String(item?.partner || '').trim(),
      percent: normalizePercent(item?.percent, maxPercent),
    })),
  };
}

export default function AdjustInstallShave({ onError }) {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [baseline, setBaseline] = useState(JSON.stringify(EMPTY_CONFIG));
  const [maxPercent, setMaxPercent] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return axios
      .get('/api/settings/adjust-install-shave')
      .then((res) => {
        const limit = Number(res.data?.maxPercent) || 20;
        const next = normalizeShaveConfig(res.data, limit);
        setMaxPercent(limit);
        setConfig(next);
        setBaseline(JSON.stringify(next));
      })
      .catch((err) => onError?.(err))
      .finally(() => setLoading(false));
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const normalized = useMemo(() => normalizeShaveConfig(config, maxPercent), [config, maxPercent]);
  const groupedPartners = useMemo(
    () => new Set(normalized.groups.flatMap((group) => group.partners.map((partner) => partner.toLowerCase()))),
    [normalized.groups],
  );
  const manualOverrides = config.overrides
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !groupedPartners.has(String(item.partner || '').trim().toLowerCase()));
  const dirty = JSON.stringify(normalized) !== baseline;

  const updateRule = (index, field, value) => {
    setSaved(false);
    setConfig((current) => ({
      ...current,
      overrides: current.overrides.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const save = async () => {
    const partners = normalized.overrides.map((item) => item.partner.toLowerCase());
    const invalid = normalized.overrides.some((item) => !item.partner || /[&,\s]/.test(item.partner));
    const duplicate = new Set(partners).size !== partners.length;

    if (invalid || duplicate) {
      window.alert(invalid
        ? 'Ключ партнера не може бути порожнім або містити пробіли чи &.'
        : 'Ключі партнерів не повинні повторюватися.');
      return;
    }

    try {
      setSaving(true);
      setSaved(false);
      const res = await axios.put('/api/settings/adjust-install-shave', normalized);
      const next = normalizeShaveConfig(res.data, maxPercent);
      setConfig(next);
      setBaseline(JSON.stringify(next));
      setSaved(true);
    } catch (err) {
      onError?.(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="app-editor-card compact-card"><div className="settings-empty">Завантаження…</div></div>;
  }

  return (
    <div className="app-editor-card compact-card shave-card">
      <div className="app-editor-card-body compact-card-body">
        <div className="shave-head">
          <div>
            <div className="stats-section-title">Adjust install cut</div>
            <div className="stats-section-subtitle">
              Лише Android-кліки з naming, знайденим через callback Adjust
            </div>
          </div>
          <div className={`shave-state${normalized.defaultPercent > 0 || normalized.overrides.some((item) => item.percent > 0) ? ' is-active' : ''}`}>
            {normalized.defaultPercent > 0 || normalized.overrides.some((item) => item.percent > 0) ? 'Активний' : 'Вимкнений'}
          </div>
        </div>

        <div className="shave-default">
          <label className="app-editor-label compact-label" htmlFor="shave-default-percent">
            Відсоток за замовчуванням
          </label>
          <div className="shave-percent-control">
            <input
              id="shave-default-percent"
              type="number"
              min="0"
              max={maxPercent}
              step="0.1"
              value={config.defaultPercent}
              onChange={(event) => {
                setSaved(false);
                setConfig((current) => ({ ...current, defaultPercent: event.target.value }));
              }}
              className="form-control compact-input"
            />
            <span>%</span>
          </div>
          <div className="shave-help">0% не змінює поточний трафік. Максимум — {maxPercent}%.</div>
        </div>

        {config.groups.length ? (
          <>
            <div className="shave-rules-head">
              <div>
                <div className="stats-section-title">Команди</div>
                <div className="stats-section-subtitle">Автоматично синхронізовано з Admin CRM</div>
              </div>
            </div>
            <div className="shave-groups">
              {config.groups.map((group) => (
                <div className="shave-group-rule" key={group.groupId}>
                  <div className="shave-group-name">{group.name}</div>
                  <div className="shave-group-partners" title={group.partners.join(', ')}>
                    {group.partners.join(', ')}
                  </div>
                  <div className="shave-group-percent">{group.percent}%</div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <div className="shave-rules-head">
          <div>
            <div className="stats-section-title">Окремі винятки</div>
            <div className="stats-section-subtitle">Ключ — перша частина naming до символу &amp;</div>
          </div>
          <button
            type="button"
            className="fbtn"
            onClick={() => {
              setSaved(false);
              setConfig((current) => ({
                ...current,
                overrides: [...current.overrides, { partner: '', percent: 0 }],
              }));
            }}
          >
            Додати партнера
          </button>
        </div>

        <div className="shave-rules">
          {manualOverrides.map(({ item, index }) => (
            <div className="shave-rule" key={`${index}-${item.partner}`}>
              <input
                type="text"
                value={item.partner}
                onChange={(event) => updateRule(index, 'partner', event.target.value)}
                placeholder="bndrnt000733"
                aria-label={`Ключ партнера ${index + 1}`}
                className="form-control compact-input"
              />
              <div className="shave-percent-control">
                <input
                  type="number"
                  min="0"
                  max={maxPercent}
                  step="0.1"
                  value={item.percent}
                  onChange={(event) => updateRule(index, 'percent', event.target.value)}
                  aria-label={`Відсоток партнера ${index + 1}`}
                  className="form-control compact-input"
                />
                <span>%</span>
              </div>
              <button
                type="button"
                className="fbtn shave-remove"
                aria-label={`Видалити партнера ${item.partner || index + 1}`}
                onClick={() => {
                  setSaved(false);
                  setConfig((current) => ({
                    ...current,
                    overrides: current.overrides.filter((_, itemIndex) => itemIndex !== index),
                  }));
                }}
              >
                Видалити
              </button>
            </div>
          ))}

          {!manualOverrides.length ? (
            <div className="settings-empty shave-empty">Окремих правил немає</div>
          ) : null}
        </div>

        <div className="shave-foot">
          <div className="shave-help">
            Якщо налаштування недоступне або ідентифікатора кліка немає, install відправляється у Adjust.
          </div>
          <div className="d-flex align-items-center gap-2">
            {saved ? <span className="shave-saved">Збережено</span> : null}
            <button type="button" className="fbtn is-primary" disabled={!dirty || saving} onClick={save}>
              {saving ? 'Збереження…' : 'Зберегти'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
